<template>
  <Modal
    :modal-active="isOpen"
    :has-header-border="true"
    :has-footer-border="true"
    max-width="max-w-6xl"
    max-height="90vh"
    @close="emit('update:isOpen', false)"
  >
    <template #header>
      <h2 class="text-lg text-left text-textPrimary">Evidência do registro de plantão</h2>
    </template>

    <div class="flex flex-col lg:flex-row gap-6 h-full">
      <div class="flex-1 min-w-0 flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <span v-if="id" class="text-xl text-textPrimary bg-gray-200 py-1 px-2 rounded-lg">{{
            id
          }}</span>
          <span class="text-xl font-semibold text-textPrimary truncate">{{
            currentFile?.name ?? fileName
          }}</span>

          <OutlineButton
            text="Ver registro"
            width="160px"
            :prepend-icon="mdiOpenInNew"
            class="ml-auto"
            height="32px"
            @click="openRegistry"
          />
        </div>

        <StepPreview
          :file="file"
          :file-url="currentFileUrl"
          :file-type="currentFileType"
          :rotation="rotation"
          :zoom="zoom"
          mode="preview"
          @update:rotation="rotation = $event"
          @update:zoom="zoom = $event"
          :show-display-name="false"
        />
      </div>

      <div class="w-full lg:w-[280px] flex flex-col gap-4 shrink-0">
        <div class="flex flex-col gap-1 text-left">
          <span class="text-base font-bold leading-6 tracking-[0.08px] text-[#16161D]">
            Responsável pelo envio
          </span>
          <span class="text-sm text-textPrimary">{{ uploader || 'Não identificado' }}</span>
        </div>

        <div class="flex flex-col gap-1 text-left">
          <span class="text-base font-bold leading-6 tracking-[0.08px] text-[#16161D]">
            Modalidade de registro
          </span>
          <span class="text-sm text-textPrimary">{{ monitoringTypeLabel }}</span>
        </div>

        <div v-if="files.length > 1" class="flex flex-col gap-1 text-left">
          <span class="text-base font-bold leading-6 tracking-[0.08px] text-[#16161D]">
            Arquivos ({{ files.length }})
          </span>
          <ul class="flex flex-col gap-1">
            <li
              v-for="(evidenceFile, index) in files"
              :key="evidenceFile.path"
              class="flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer text-sm transition-colors"
              :class="
                index === selectedIndex
                  ? 'bg-primary30 text-white font-semibold'
                  : 'hover:bg-gray-100 text-textPrimary'
              "
              data-testid="evidence-file-item"
              @click="selectFile(index)"
            >
              <v-icon :icon="mdiFile" size="16" />
              <span class="truncate">{{ evidenceFile.name }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <OutlineButton
          data-testid="evidence-close-button"
          text="Fechar"
          width="100px"
          height="32px"
          @click="emit('update:isOpen', false)"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { mdiOpenInNew, mdiFile } from '@mdi/js'
import Modal from '@/components/designSystem/modals/Modal.vue'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import type { HistoryEvent } from '@/components/designSystem/Cards/DsAttendanceHistoryCard.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  DSAttendanceStatus,
  EXTERNAL_APP_LABELS,
  ExternalApp
} from '@/constants/ds-attendance.constants'
import { ExceptionStatusEnum } from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import type { CheckEvidence } from '@/Network/Types/Requests/StationRequests'

const props = defineProps<{
  isOpen: boolean
  id: string
  fileName: string
  file: File | null
  fileUrl?: string
  files: CheckEvidence[]
  uploader: string
  monitoringType: string
  sourceApp?: string
  status: DSAttendanceStatus | ExceptionStatusEnum
  historyEvents: HistoryEvent[]
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
}>()

const rotation = ref(0)
const zoom = ref(1)
const selectedIndex = ref(0)

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      selectedIndex.value = 0
      rotation.value = 0
      zoom.value = 1
    }
  }
)

// `sourceApp` chega cru do backend (`DOCTOR_ID`, `PEGA_PLANTAO`) e era concatenado
// sem tradução, vazando o enum na tela. `EXTERNAL_APP_LABELS` já existia para isso.
// Não usamos `formatMonitoringTypeLabel` porque ela só anexa o app quando
// `monitoringType === 'EXTERNAL_APP'`, e aqui a prop já chega traduzida.
const monitoringTypeLabel = computed(() => {
  const base = props.monitoringType || 'Não identificado'
  if (!props.sourceApp) return base
  const appLabel = EXTERNAL_APP_LABELS[props.sourceApp as ExternalApp] ?? props.sourceApp
  return `${base} - ${appLabel}`
})

const currentFile = computed(() => props.files[selectedIndex.value] ?? null)

const currentFileUrl = computed(() => {
  if (currentFile.value) return currentFile.value.path
  return props.fileUrl
})

const currentFileType = computed(() => currentFile.value?.type)

function selectFile(index: number) {
  selectedIndex.value = index
  rotation.value = 0
  zoom.value = 1
}

const openRegistry = () => {
  const url = currentFile.value?.path
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}
</script>
